import {trackComponent} from '@aboutyou/legacy-tracking-sdk';
import {WithOptionalTypes} from '@aboutyou/utils';
import {useCallback, useRef} from 'react';
import {useTrackingPageContext} from '../context/PageTrackingContext';
import {useTracking} from '../utils/utils';
// eslint-disable-next-line no-restricted-imports
import {useInViewEffect} from '@aboutyou/ui-components';

type TrackComponentCallbackType = Omit<
  Parameters<typeof trackComponent>[0],
  'details' | 'abtest_flag' | 'event_scope' | 'object_details' | 'event'
>;

type TrackComponentCallbackTypePartial = WithOptionalTypes<
  TrackComponentCallbackType,
  'sponsored' | 'page_type_id' | 'page_type'
>;

export type TrackComponentParamsType = Pick<
  TrackComponentCallbackTypePartial,
  'location_id' | 'page_type_id'
>;

export const useTrackComponent = () => {
  const {TrackingClient} = useTracking();
  const {type: pageTypeContext, id} = useTrackingPageContext();

  return useCallback(
    ({
      page_type,
      page_type_id = '',
      sponsored = false,
      ...rest
    }: TrackComponentCallbackTypePartial) => {
      TrackingClient.track(
        trackComponent({
          page_type: page_type ?? pageTypeContext,
          page_type_id: page_type_id || (id ? id.toString() : ''),
          sponsored,
          ...rest,
        }),
      );
    },
    [TrackingClient, id, pageTypeContext],
  );
};

export const useTrackComponentImpression = (
  params: Omit<TrackComponentCallbackTypePartial, 'action'>,
) => {
  const trackComponent = useTrackComponent();
  const hasTrackedRef = useRef(false);
  const paramsRef = useRef(params);
  const trackComponentRef = useRef(trackComponent);

  paramsRef.current = params;
  trackComponentRef.current = trackComponent;

  const handleImpression = useCallback((inView: boolean) => {
    if (!inView || hasTrackedRef.current) {
      return;
    }

    hasTrackedRef.current = true;
    trackComponentRef.current({
      ...paramsRef.current,
      action: 'impression',
    });
  }, []);

  const {ref} = useInViewEffect<HTMLDivElement>(handleImpression, {
    triggerOnce: true,
  });

  return {ref};
};
